import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import useApi from 'api/useApi'
import { useOrg } from 'contexts/OrgContext'
import { QueryKey } from 'hooks/data/queryKeys'
import { useOrgQueryKey } from 'hooks/utils/useOrgQueryKey'

import { useOrgSetting } from './settingsHooks'

export default function useLegalEntity({ enabled = true } = {}) {
  const { org } = useOrg()
  const enableLegalEntities = useOrgSetting('enable_legal_entities', { enabled })
  const { fetch_user_org_entities } = useApi()
  const legalEntityQueryKey = useOrgQueryKey(QueryKey.LegalEntity)

  const { isLoading: isLoadingEntity, data } = useQuery(
    legalEntityQueryKey,
    fetch_user_org_entities,
    { enabled }
  )

  const canDeleteOrCreateLegalEntity =
    enableLegalEntities.value && (!org || !data?.entities.includes(org))

  return {
    entityData: data?.entities,
    rootOrg: data?.rootOrg,
    isLoadingEntity,
    canDeleteOrCreateLegalEntity,
  }
}

export const useCreateLegalEntity = () => {
  const queryClient = useQueryClient()
  const { create_legal_entity } = useApi()

  return useMutation(async (entityName: string) => create_legal_entity(entityName), {
    onSuccess: () => {
      void queryClient.invalidateQueries([QueryKey.LegalEntity])
      void queryClient.invalidateQueries([QueryKey.OrgsByParent])
    },
  })
}

export const useDeleteLegalEntity = () => {
  const queryClient = useQueryClient()
  const { delete_legal_entity } = useApi()

  return useMutation(async (entityName: string) => delete_legal_entity(entityName), {
    onSuccess: () => {
      void queryClient.invalidateQueries([QueryKey.LegalEntity])
      void queryClient.invalidateQueries([QueryKey.OrgsByParent])
    },
  })
}
